import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown, Plus, Trash2, Upload, UserRound, X } from "lucide-react";
import { useFacility } from "../../../context/FacilityContext";
import { Avatar, BulkBar, ConfirmDialog, DataTable, EmptyRow, FilterSelect, SearchBox, StatusBadge, UndoToast, statusDot } from "../../components/crm/ui";
import {
  ColumnPicker,
  NO_DATA,
  PRIORITIES,
  PriorityBadge,
  RowActions,
  SOURCES,
  ScoreMeter,
  SourceLabel,
  StatusPicker,
  apiDelete,
  apiGet,
  apiPost,
  apiPut,
  fmtFollowUp,
  fmtCreated,
  fmtPhone,
  fullName,
  initialsOf,
  isoDay,
  priorityKey,
  sourceKey,
  sourceLabel,
  priorityLabel,
  statusKeyOf,
  statusOptions,
  toLead,
  useColumns,
} from "../../components/crm/leadsKit";
import ImportDialog from "../../components/crm/ImportDialog";
import { LeadFilterBuilder, filterLabel } from "../../components/crm/LeadFilterBuilder";
import { VIEW_ONLY_TITLE, ViewOnlyNote, useCrmAccess } from "../../components/crm/crmAccess";
import { AccessNotice, fetchApi, reasonOf } from "../../components/marketing/access";
import { StatCards } from "../../components/crm/StatCards";
import { PrimaryButton } from "../../components/ui";
import { InlineLoader, PageLoader } from "../../components/PageLoader";
import { SearchSelect } from "../../components/SearchSelect";
import { Pagination, usePagination } from "../../components/wallet/parts";
import { DAY, fmtAgo } from "../../lib/format";

/**
 * CRM → Leads (design: screens/crm/Leads.tsx).
 *
 * Real data and actions:
 *   leads table, filters, search, sort, pages ... GET /api/leads (status, source, assigned_to, priority, search, sort_by, page, facility_id)
 *   lead stats ................................... GET /api/leads/stats (facility_id)
 *   new today, this week, 14-day trend ............ GET /api/lead-analytics/pivot (row=created_day)
 *   statuses ...................................... GET /api/lead-statuses
 *   assignees, preferred doctors .................. GET /api/staff/assignable-users, GET /api/staff?role=doctor
 *   shown columns ................................. GET/PUT /api/view-preferences/leads_table
 *   status change (and undo), assign, bulk status,
 *   bulk assign ................................... PUT /api/leads/:id
 *   delete, bulk delete (soft) .................... DELETE /api/leads/:id
 *   undo a delete ................................. POST /api/leads/:id/restore (item 141)
 *   City column, sortable ......................... contact_city on GET /api/leads rows (item 142)
 *   import ........................................ ImportDialog (POST /api/leads/import, items 150–152)
 *   tag and "has campaign" filters ............. tag_id, has_campaign on GET /api/leads; tags from GET /api/tags
 *   more filters (any field, any operator) ...... filters=[…] on GET /api/leads; fields and operators
 *                                                 from GET /api/view-preferences/columns (LeadFilterBuilder)
 *   filters in the address ....................... ?status=&source=&priority=&assigned_to=&tag_id=&has_campaign=
 *                                                 &follow_up=&q=&filters=, so a link (or a Lead reports cell)
 *                                                 opens the list already filtered
 *   view only (staff CRM permission) ............. useCrmAccess: status menu, edit, delete, bulk, import off
 * Stat cards count per pipeline stage (stats.byStatusKey, the org's own status_key, legacy rows under
 * their old status): each stage, custom ones included, counts once under the system stage it maps to
 * (lead-statuses maps_to_system). An older backend without byStatusKey falls back to stats.byStatus.
 * Every column, Preferred doctor / Tags / UTM campaign / City included, is saved
 * to leads_table (item 143). Campaign and UTM campaign show the same field
 * (utm_campaign), so they share one saved key and come back as UTM campaign.
 */

/** Design column id → leads_table preference key (GET/PUT /api/view-preferences). */
const PREF_KEY = {
  name: "first_name", phone: "phone", email: "email", status: "status", source: "source", campaign: "utm_campaign", service: "interested_service",
  priority: "priority", score: "lead_score", assignee: "assigned_to_name", followup: "next_follow_up_date", created: "created_at", updated: "updated_at",
  facility: "facility_name", budget: "budget_range", "utm-source": "utm_source", "utm-medium": "utm_medium", "ad-name": "ad_name", "form-name": "meta_form_name",
  doctor: "preferred_doctor", tags: "tags", "utm-campaign": "utm_campaign", city: "contact_city",
};
// Later entries win, so a saved utm_campaign restores the UTM campaign column.
const PREF_ID = Object.fromEntries(Object.entries(PREF_KEY).map(([id, key]) => [key, id]));
/** Design column id → GET /api/leads sort_by (config/leadColumns.js sortable keys). */
const SORT_KEY = { ...PREF_KEY };
delete SORT_KEY.phone;
delete SORT_KEY.budget;
delete SORT_KEY.tags;

const OPEN_SYSTEM = ["new", "contacted", "qualified", "proposal", "negotiation"];

export default function Leads() {
  const fac = useFacility();
  const facilityId = fac.apiParams().facility_id || null;
  const facilityLabel = fac.currentFacility ? fac.currentFacility.name : "All locations";
  const navigate = useNavigate();
  const [statuses, setStatuses] = useState([]);
  const [staff, setStaff] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [assignee, setAssignee] = useState("");
  const [priority, setPriority] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [tag, setTag] = useState("");
  const [hasCampaign, setHasCampaign] = useState("");
  const [advanced, setAdvanced] = useState([]); // more filters: [{ column, operator, value, value2 }]
  const [tags, setTags] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [listError, setListError] = useState(null);
  const [params, setParams] = useSearchParams();
  const initialParams = useRef(params);
  const [hydrated, setHydrated] = useState(false); // filters read from the address once the vocabularies are in
  const [statusesLoaded, setStatusesLoaded] = useState(false);
  const [staffLoaded, setStaffLoaded] = useState(false);
  const access = useCrmAccess();
  const canEdit = access.canEdit;
  const [query, setQuery] = useState(() => params.get("q") || "");
  const [search, setSearch] = useState(() => (params.get("q") || "").trim());
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkMenu, setBulkMenu] = useState(null);
  const [undoToast, setUndoToast] = useState(null);
  const [storedCols, setStoredCols] = useState(undefined);
  const [sort, setSort] = useState({ id: "created", dir: -1 });
  const [rawRows, setRawRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loadedKey, setLoadedKey] = useState(null); // the filters and page the rows on screen were loaded for
  const [reload, setReload] = useState(0);
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState(null);
  const [statsFor, setStatsFor] = useState(undefined); // the location stats / daily were loaded for
  const [dailyFor, setDailyFor] = useState(undefined);
  const refresh = useCallback(() => setReload((n) => n + 1), []);
  const clearToast = useCallback(() => setUndoToast(null), []);

  useEffect(() => {
    apiGet("/api/lead-statuses").then((r) => { setStatuses(statusOptions(r?.data)); setStatusesLoaded(true); });
    apiGet("/api/staff/assignable-users").then((r) => { setStaff((r?.data || []).filter((u) => u.user_id && u.name).map((u) => ({ id: u.user_id, staffId: u.staff_id, name: u.name }))); setStaffLoaded(true); });
    apiGet("/api/tags").then((r) => setTags((r?.data || []).filter((t) => t.id && t.name)));
    apiGet("/api/view-preferences/columns").then((r) => setCatalog(r?.data?.columns || []));
    apiGet("/api/staff", { is_active: true, role: "doctor" }).then((r) => setDoctors((r?.data || []).map((d) => ({ id: d.id, name: d.user_name || `${d.first_name || ""} ${d.last_name || ""}`.trim() }))));
    apiGet("/api/view-preferences/leads_table").then((r) => {
      const d = r?.data;
      if (!d || d.is_default) return;
      const ids = (d.visible_columns || []).map((k) => PREF_ID[k]).filter(Boolean);
      if (!ids.length) return;
      const withName = ids.includes("name") ? ids : ["name", ...ids];
      // Status goes where Priority was: swap them once, then keep the saved order.
      const si = withName.indexOf("status");
      const pi = withName.indexOf("priority");
      let swapped = false;
      try { swapped = localStorage.getItem("olli.leads.statusFirst") === "1"; } catch { /* storage unavailable */ }
      if (!swapped && si > -1 && pi > -1 && pi < si) {
        const next = [...withName];
        next[pi] = "status";
        next[si] = "priority";
        setStoredCols(next);
        const keys = [...new Set(next.map((id) => PREF_KEY[id]).filter(Boolean))];
        // The flag is set once the new order is saved, so a failed save tries again next time.
        apiPut("/api/view-preferences/leads_table", { visible_columns: keys, sort_by: d.sort_by || "created_at", sort_order: d.sort_order || "DESC" })
          .then(() => { try { localStorage.setItem("olli.leads.statusFirst", "1"); } catch { /* storage unavailable */ } })
          .catch(() => {});
        return;
      }
      setStoredCols(withName);
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Filters from the address (a shared link, or a Lead reports cell), once the
  // stage, people and tag names are known; from then on the address follows the filters.
  useEffect(() => {
    if (hydrated || !statusesLoaded || !staffLoaded || tags === null) return;
    const p = initialParams.current;
    const st = p.get("status");
    if (st) setStatus(statuses.find((s) => s.key === st)?.name || st);
    if (p.get("source")) setSource(sourceLabel(p.get("source")));
    if (p.get("priority")) setPriority(priorityLabel(p.get("priority")));
    const at = p.get("assigned_to");
    if (at) setAssignee(at === "unassigned" ? "Unassigned" : staff.find((s) => String(s.staffId) === at)?.name || "");
    if (p.get("tag_id")) setTag(tags.find((t) => String(t.id) === p.get("tag_id"))?.name || "");
    if (["yes", "no"].includes(p.get("has_campaign"))) setHasCampaign(p.get("has_campaign") === "yes" ? "With a campaign" : "Without a campaign");
    if (p.get("follow_up") === "today") setFollowUp("today");
    try { const f = JSON.parse(p.get("filters") || "[]"); if (Array.isArray(f)) setAdvanced(f.filter((x) => x && x.column && x.operator)); } catch (_) { /* not a filter list */ }
    setHydrated(true);
  }, [hydrated, statusesLoaded, staffLoaded, tags, statuses, staff]);
  const tagId = tags?.find((t) => t.name === tag)?.id;
  useEffect(() => {
    if (!hydrated) return;
    const next = new URLSearchParams();
    const statusKey = status ? statusKeyOf(statuses, status) || status : "";
    if (statusKey) next.set("status", statusKey);
    if (source) next.set("source", sourceKey(source));
    if (priority) next.set("priority", priorityKey(priority));
    const staffRow = staff.find((s) => s.name === assignee);
    if (assignee) next.set("assigned_to", assignee === "Unassigned" ? "unassigned" : String(staffRow?.staffId || ""));
    if (tagId) next.set("tag_id", String(tagId));
    if (hasCampaign) next.set("has_campaign", hasCampaign === "With a campaign" ? "yes" : "no");
    if (followUp) next.set("follow_up", followUp);
    if (search) next.set("q", search);
    if (advanced.length) next.set("filters", JSON.stringify(advanced));
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [hydrated, status, source, priority, assignee, tagId, hasCampaign, followUp, search, advanced]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stats for the location: totals by status, follow-ups, and leads per day for the trend.
  useEffect(() => {
    let alive = true;
    const today = new Date().setHours(0, 0, 0, 0);
    apiGet("/api/leads/stats", facilityId ? { facility_id: facilityId } : {}).then((r) => { if (alive) { setStats(r?.success ? r.data : null); setStatsFor(facilityId); } });
    apiGet("/api/lead-analytics/pivot", { row: "created_day", measure: "count", date_from: isoDay(today - 13 * DAY), date_to: isoDay(today), ...(facilityId ? { facility_id: facilityId } : {}) }).then((r) => {
      if (!alive) return;
      setDailyFor(facilityId);
      if (!r?.success) return setDaily(null);
      const byDay = {};
      Object.entries(r.data.cells || {}).forEach(([k, c]) => { byDay[k.split("||")[0]] = Number(c.count) || 0; });
      setDaily(Array.from({ length: 14 }, (_, i) => byDay[isoDay(today - (13 - i) * DAY)] || 0));
    });
    return () => { alive = false; };
  }, [facilityId, reload]);

  const leads = useMemo(() => rawRows.map((row) => toLead(row, statuses, doctors)), [rawRows, statuses, doctors]);
  const nameOf = (l) => fullName(l) || "Unknown contact";

  const changeStatus = useCallback(async (lead, next) => {
    const nextKey = statusKeyOf(statuses, next);
    const previousKey = lead.statusKey;
    const previous = lead.status;
    const name = fullName(lead) || "Unknown contact";
    try {
      await apiPut(`/api/leads/${lead.id}`, { status: nextKey });
      refresh();
      setUndoToast({
        message: `Status changed to ${next} for ${name}`,
        onUndo: () => apiPut(`/api/leads/${lead.id}`, { status: previousKey }).then(refresh).catch(() => setUndoToast({ message: `Couldn't change ${name} back to ${previous}` })),
      });
    } catch (e) {
      setUndoToast({ message: `Couldn't change the status for ${name}` });
    }
  }, [statuses, refresh]);

  const allColumns = useMemo(() => [
    { id: "name", label: "Name", locked: true, default: true, width: "minmax(220px,1.5fr)", sort: SORT_KEY.name, render: (l) => { const name = fullName(l) || "Unknown contact"; return <span className="flex min-w-0 items-center gap-3"><Avatar name={name} initials={initialsOf(l) || "?"} size="sm" /><span className="flex min-w-0 flex-col"><span className="truncate font-medium">{name}</span></span></span>; } },
    { id: "phone", label: "Phone", default: true, render: (l) => <span className="tabular-nums">{fmtPhone(l.phone)}</span> },
    { id: "email", label: "Email", sort: SORT_KEY.email, render: (l) => l.email || "Not added" },
    { id: "status", label: "Status", default: true, sort: SORT_KEY.status, render: (l) => canEdit ? <StatusPicker status={l.status} label={fullName(l) || "Unknown contact"} options={statuses} onChange={(next) => changeStatus(l, next)} /> : <StatusBadge status={l.status} /> },
    { id: "source", label: "Source", default: true, sort: SORT_KEY.source, render: (l) => <SourceLabel source={l.source} /> },
    { id: "campaign", label: "Campaign", sort: SORT_KEY.campaign, render: (l) => l.utm.campaign || "Not added" },
    { id: "service", label: "Interested service", default: true, sort: SORT_KEY.service, render: (l) => l.service || "Not added" },
    { id: "priority", label: "Priority", sort: SORT_KEY.priority, render: (l) => <PriorityBadge priority={l.priority} /> },
    { id: "score", label: "Score", sort: SORT_KEY.score, align: "right", render: (l) => <ScoreMeter score={l.score} /> },
    { id: "assignee", label: "Assigned to", default: true, sort: SORT_KEY.assignee, render: (l) => l.assignee || <span className="text-muted">Unassigned</span> },
    { id: "followup", label: "Next follow-up", default: true, sort: SORT_KEY.followup, render: (l) => l.nextFollowUp ? <span className={l.nextFollowUp < new Date().setHours(0, 0, 0, 0) ? "text-danger" : ""}>{fmtFollowUp(l.nextFollowUp)}</span> : <span className="text-muted">Not set</span> },
    { id: "created", label: "Created", default: true, sort: SORT_KEY.created, render: (l) => { const [when, at] = fmtCreated(l.createdAt); return <span className="flex flex-col whitespace-nowrap"><span>{when}</span>{at && <span className="text-caption text-muted">{at}</span>}</span>; } },
    { id: "updated", label: "Last updated", sort: SORT_KEY.updated, render: (l) => fmtAgo(l.updatedAt, Date.now(), true) },
    { id: "facility", label: "Facility", sort: SORT_KEY.facility, render: (l) => l.facility || NO_DATA },
    { id: "doctor", label: "Preferred doctor", sort: SORT_KEY.doctor, render: (l) => l.doctor || "Any doctor" },
    { id: "budget", label: "Budget", render: (l) => l.budget || "Not added" },
    { id: "tags", label: "Tags", render: (l) => <span>{l.tags.slice(0, 2).join(", ")}{l.tags.length > 2 ? ` +${l.tags.length - 2}` : ""}</span> },
    { id: "utm-source", label: "UTM source", sort: SORT_KEY["utm-source"], render: (l) => l.utm.source || "Not added" },
    { id: "utm-medium", label: "UTM medium", sort: SORT_KEY["utm-medium"], render: (l) => l.utm.medium || "Not added" },
    { id: "utm-campaign", label: "UTM campaign", sort: SORT_KEY["utm-campaign"], render: (l) => l.utm.campaign || "Not added" },
    { id: "ad-name", label: "Ad name", sort: SORT_KEY["ad-name"], render: (l) => l.adName || "Not added" },
    { id: "form-name", label: "Form name", sort: SORT_KEY["form-name"], render: (l) => l.formName || "Not added" },
    // The linked contact's city (item 142).
    { id: "city", label: "City", sort: SORT_KEY.city, render: (l) => l.city || NO_DATA },
  ], [statuses, changeStatus, canEdit]);

  const saveColumns = (ids) => {
    setStoredCols(ids);
    const keys = [...new Set(ids.map((id) => PREF_KEY[id]).filter(Boolean))];
    apiPut("/api/view-preferences/leads_table", { visible_columns: keys.length ? keys : ["first_name"], sort_by: SORT_KEY[sort?.id] || "created_at", sort_order: sort?.dir === 1 ? "ASC" : "DESC" }).catch(() => {});
  };
  const cols = useColumns(allColumns, storedCols, saveColumns);

  const tableRef = useRef(null);
  const pg = usePagination(total, JSON.stringify({ status, source, assignee, priority, followUp, tagId, hasCampaign, advanced, search, sort, facilityId }));

  // The page of leads for the current filters, sort and page. Until the answer
  // for the current ones arrives the table shows the loader; a refresh on the
  // same filters (after a status change, delete…) keeps the rows on screen.
  const listKey = JSON.stringify({ page: pg.page, size: pg.size, sort, status, source, priority, assignee, followUp, tagId, hasCampaign, advanced, search, facilityId });
  const loading = !hydrated || loadedKey !== listKey;
  useEffect(() => {
    if (!hydrated) return undefined;
    let alive = true;
    const key = listKey;
    const staffRow = staff.find((s) => s.name === assignee);
    const params = {
      page: pg.page,
      limit: pg.size,
      sort_by: SORT_KEY[sort?.id] || "created_at",
      sort_order: sort ? (sort.dir === 1 ? "ASC" : "DESC") : "DESC",
      ...(status ? { status: statusKeyOf(statuses, status) || status } : {}),
      ...(source ? { source: sourceKey(source) } : {}),
      ...(priority ? { priority: priorityKey(priority) } : {}),
      ...(assignee ? { assigned_to: assignee === "Unassigned" ? "unassigned" : staffRow?.staffId || "-1" } : {}),
      ...(followUp ? { follow_up: followUp } : {}),
      ...(tagId ? { tag_id: tagId } : {}),
      ...(hasCampaign ? { has_campaign: hasCampaign === "With a campaign" ? "yes" : "no" } : {}),
      ...(advanced.length ? { filters: JSON.stringify(advanced) } : {}),
      ...(search ? { search } : {}),
      ...(facilityId ? { facility_id: facilityId } : {}),
    };
    fetchApi("/api/leads", { params }).then((r) => {
      if (!alive) return;
      setListError(r.ok ? null : reasonOf(r));
      setRawRows(r.ok ? r.data?.data || [] : []);
      setTotal(r.ok ? Number(r.data?.pagination?.total) || 0 : 0);
      setLoadedKey(key);
    });
    return () => { alive = false; };
  }, [hydrated, pg.page, pg.size, sort, status, source, priority, assignee, followUp, tagId, hasCampaign, advanced, search, facilityId, statuses, staff, reload]); // eslint-disable-line react-hooks/exhaustive-deps
  const statsLoading = statsFor !== facilityId;
  const dailyLoading = dailyFor !== facilityId;

  useEffect(() => {
    const visibleIds = new Set(leads.map((lead) => lead.id));
    const next = new Set([...selected].filter((id) => visibleIds.has(id)));
    if (next.size !== selected.size) setSelected(next);
  }, [leads, selected]);
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      if (bulkMenu) setBulkMenu(null);
      else if (selected.size) setSelected(new Set());
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [bulkMenu, selected]);

  const freshToday = daily ? daily[daily.length - 1] : null;
  const allTotal = stats ? Number(stats.total) || 0 : null;
  const statsCards = useMemo(() => {
    if (!stats) return [];
    // Stage key → system stage; a key the clinic hasn't configured (legacy status) is its own system stage.
    const byKey = stats.byStatusKey || stats.byStatus || {};
    const systemOf = (key) => statuses.find((s) => s.key === key)?.system || key;
    const count = (systems) => Object.entries(byKey).reduce((n, [k, c]) => n + (systems.includes(systemOf(k)) ? Number(c) || 0 : 0), 0);
    const totalLeads = Number(stats.total) || 0;
    const fresh = count(["new"]);
    const open = count(OPEN_SYSTEM);
    const won = count(["won"]);
    const overdue = Number(stats.overdueFollowups) || 0;
    const dueToday = Number(stats.todayFollowups) || 0;
    const week = daily ? daily.slice(-7).reduce((a, b) => a + b, 0) : null;
    const newName = statuses.find((s) => s.key === "new")?.name || "New";
    const wonName = statuses.find((s) => s.key === "won")?.name || "Won";
    return [
      { id: "total", label: "Total leads", value: totalLeads.toLocaleString("en-IN"), note: dailyLoading ? <InlineLoader size={14} /> : <>{freshToday ?? NO_DATA} new today · {week ?? NO_DATA} this week</>, onClick: () => { clearFilters(); setSort({ id: "created", dir: -1 }); }, actionLabel: "Show all leads" },
      { id: "open", label: "Open pipeline", value: open.toLocaleString("en-IN"), note: <>{fresh} not contacted yet</>, onClick: () => { setStatus(newName); setSort({ id: "created", dir: -1 }); }, actionLabel: "Show new leads" },
      { id: "won", label: "Converted", value: `${totalLeads ? Math.round((won / totalLeads) * 100) : 0}%`, note: <>{won} of {totalLeads} leads</>, onClick: () => { setStatus(wonName); setSort({ id: "created", dir: -1 }); }, actionLabel: "Show converted leads" },
      { id: "followups", label: "Follow-ups overdue", value: overdue.toLocaleString("en-IN"), note: overdue ? <><span className="font-semibold text-danger">Past due</span> · {dueToday} more due today</> : <>{dueToday} due today</>, onClick: () => setSort({ id: "followup", dir: 1 }), actionLabel: "Sort by next follow-up" },
      { id: "today", label: "Today's follow-ups", value: dueToday.toLocaleString("en-IN"), note: dailyLoading ? <InlineLoader size={14} /> : <>{freshToday ?? NO_DATA} new leads today</>, onClick: () => { clearFilters(); setFollowUp("today"); setSort({ id: "created", dir: -1 }); }, actionLabel: "Show today's follow-ups", highlight: "sky" },
    ];
  }, [stats, daily, dailyLoading, freshToday, statuses]);

  const clearFilters = () => { setStatus(""); setSource(""); setAssignee(""); setPriority(""); setFollowUp(""); setTag(""); setHasCampaign(""); setAdvanced([]); setQuery(""); };
  const filtersOn = !!(status || source || assignee || priority || followUp || tag || hasCampaign || advanced.length || search);
  const empty = !filtersOn && total === 0
    ? <EmptyRow title="No leads yet" body="Leads will appear here when Meta lead ads, website forms and WhatsApp are connected." action={<PrimaryButton onClick={() => navigate("/crm/leads/new")}><Plus size={17} strokeWidth={1.75} />Add lead</PrimaryButton>} />
    : <EmptyRow title="No leads match these filters" body="Try a different search or clear the filters to see more leads." action={<button onClick={clearFilters} className="flex h-10 items-center rounded-full px-5 text-small font-semibold ring-1 ring-line hover:bg-mist">Clear filters</button>} />;

  const changeSelectedStatus = async (nextStatus) => {
    const ids = [...selected];
    const nextKey = statusKeyOf(statuses, nextStatus);
    const previous = new Map();
    ids.forEach((id) => { const lead = leads.find((item) => item.id === id); if (lead) previous.set(id, lead.statusKey); });
    setBulkMenu(null);
    setSelected(new Set());
    const results = await Promise.all(ids.map((id) => apiPut(`/api/leads/${id}`, { status: nextKey }).then(() => id).catch(() => null)));
    const done = results.filter(Boolean);
    refresh();
    setUndoToast({
      message: done.length === ids.length ? `Status changed to ${nextStatus} for ${ids.length} leads` : `Status changed for ${done.length} of ${ids.length} leads`,
      onUndo: done.length ? () => Promise.all(done.map((id) => apiPut(`/api/leads/${id}`, { status: previous.get(id) }).catch(() => null))).then(refresh) : undefined,
    });
  };
  const assignSelected = async (nextAssignee) => {
    const ids = [...selected];
    const person = staff.find((s) => s.name === nextAssignee);
    setBulkMenu(null);
    setSelected(new Set());
    const results = await Promise.all(ids.map((id) => apiPut(`/api/leads/${id}`, { assigned_to: person ? person.id : null }).then(() => id).catch(() => null)));
    const done = results.filter(Boolean).length;
    refresh();
    if (done < ids.length) setUndoToast({ message: `Assigned ${done} of ${ids.length} leads` });
  };
  // Deletes are soft: Undo restores (item 141). A lead deleted with its contact
  // comes back only with the contact, and the restore says so.
  const restoreLeads = (ids) => Promise.all(ids.map((id) => apiPost(`/api/leads/${id}/restore`).then(() => id).catch((e) => ({ error: e?.response?.data?.message }))))
    .then((results) => {
      const failed = results.filter((r) => typeof r === "object" && r);
      refresh();
      if (failed.length) setUndoToast({ message: failed.length === ids.length && failed[0].error ? failed[0].error : `Restored ${ids.length - failed.length} of ${ids.length} leads` });
    });
  const deleteOne = async (lead) => {
    setConfirmDelete(null);
    try {
      await apiDelete(`/api/leads/${lead.id}`);
      setUndoToast({ message: "Lead deleted", onUndo: () => restoreLeads([lead.id]) });
    } catch (e) {
      setUndoToast({ message: "Couldn't delete this lead" });
    }
    refresh();
  };
  const deleteSelected = async () => {
    const ids = [...selected];
    setSelected(new Set());
    setBulkDeleteOpen(false);
    const results = await Promise.all(ids.map((id) => apiDelete(`/api/leads/${id}`).then(() => id).catch(() => null)));
    const deleted = results.filter(Boolean);
    const done = deleted.length;
    setUndoToast({ message: done === ids.length ? `${done} leads deleted` : `${done} of ${ids.length} leads deleted`, ...(done ? { onUndo: () => restoreLeads(deleted) } : {}) });
    refresh();
  };

  return <main className="mx-auto flex max-w-7xl flex-col gap-6 px-8 py-9 animate-fade-in">
    <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
      <div><h1 className="text-h1 font-semibold">Leads</h1><p className="mt-1 text-small text-muted">{statsLoading ? <InlineLoader size={14} /> : allTotal === null ? NO_DATA : allTotal.toLocaleString()} leads · {dailyLoading ? <InlineLoader size={14} /> : freshToday ?? NO_DATA} new today · {facilityLabel}</p></div>
      <div className="flex items-center gap-3">
        {canEdit && <button onClick={() => setImportOpen(true)} className="flex h-10 items-center gap-2 rounded-full bg-paper px-4 text-small font-semibold text-ink ring-1 ring-line hover:bg-mist"><Upload size={16} strokeWidth={1.75} />Import</button>}
        <PrimaryButton className="whitespace-nowrap" disabled={!canEdit} title={!canEdit ? VIEW_ONLY_TITLE : undefined} onClick={() => navigate("/crm/leads/new")}><Plus size={17} strokeWidth={1.75} />Add lead</PrimaryButton>
      </div>
    </header>
    {access.viewOnly && <ViewOnlyNote />}
    {!statsLoading && allTotal > 0 && <StatCards label="Lead stats" stats={statsCards} equalColumns />}
    <div className="relative z-20 flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
      <div className="flex flex-wrap items-center gap-3">
        <FilterSelect label="Status" value={status} options={statuses.map((s) => s.name)} onChange={setStatus} allLabel="All statuses" />
        <FilterSelect label="Source" value={source} options={[...SOURCES]} onChange={setSource} allLabel="All sources" />
        <FilterSelect label="Assigned to" value={assignee} options={[...staff.filter((s) => s.staffId).map((s) => s.name), "Unassigned"]} onChange={setAssignee} allLabel="Any assignee" />
        <FilterSelect label="Priority" value={priority} options={PRIORITIES} onChange={setPriority} allLabel="Any priority" />
        {tags?.length > 0 && <FilterSelect label="Tag" value={tag} options={tags.map((t) => t.name)} onChange={setTag} allLabel="Any tag" />}
        <FilterSelect label="Campaign" value={hasCampaign} options={["With a campaign", "Without a campaign"]} onChange={setHasCampaign} allLabel="Any campaign" renderValue={(v) => (v === "With a campaign" ? "Has one" : "None")} />
        <LeadFilterBuilder catalog={catalog} statuses={statuses} value={advanced} onChange={setAdvanced} />
        {followUp === "today" && <button type="button" onClick={() => setFollowUp("")} aria-label="Remove the today's follow-ups filter" className="flex h-10 items-center gap-2 rounded-full bg-ink px-4 text-small font-semibold text-paper hover:bg-ink/85">Follow-up: Today<X size={14} strokeWidth={2} /></button>}
      </div>
      <div className="flex items-center gap-3">
        <SearchBox value={query} onChange={setQuery} placeholder="Name, phone, email or campaign" className="ml-auto w-64" />
        <ColumnPicker all={allColumns} cols={cols} />
      </div>
    </div>
    {advanced.length > 0 && <div className="-mt-2 flex flex-wrap items-center gap-2" aria-label="More filters in use">{advanced.map((f, i) => <span key={i} className="flex h-8 max-w-full items-center gap-1 rounded-full bg-mist pl-3 pr-1 text-caption font-medium"><span className="truncate">{filterLabel(f, catalog, statuses)}</span><button type="button" aria-label={`Remove filter ${filterLabel(f, catalog, statuses)}`} onClick={() => setAdvanced((list) => list.filter((_, j) => j !== i))} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted hover:bg-paper hover:text-ink"><X size={12} strokeWidth={2} /></button></span>)}<button type="button" onClick={() => setAdvanced([])} className="text-caption font-semibold text-sky-deep hover:underline">Clear</button></div>}
    {loading ? <PageLoader label="Loading leads…" /> : listError ? <AccessNotice reason={listError} onRetry={refresh} /> : <section aria-label="Leads table" className="animate-fade-in">
      <div ref={tableRef}><DataTable columns={cols.visible} rows={leads} rowKey={(l) => l.id} onOpen={(l) => navigate(`/crm/leads/${l.id}`)} sort={sort} onSort={(next) => setSort(next || { id: "created", dir: -1 })} empty={empty} offset={0} limit={pg.size} selection={canEdit ? { selected, onChange: setSelected } : undefined} actions={(lead) => <RowActions label={nameOf(lead)} onView={() => navigate(`/crm/leads/${lead.id}`)} onEdit={canEdit ? () => navigate(`/crm/leads/edit/${lead.id}`) : undefined} onDelete={canEdit ? () => setConfirmDelete(lead) : undefined} />} /></div>
      {total > 0 && <Pagination state={pg} noun="lead" scrollTo={tableRef} />}
    </section>}
      {confirmDelete && <ConfirmDialog title="Delete this lead?" body={<>{nameOf(confirmDelete)}'s lead {confirmDelete.ref} will be removed. The contact stays in Contacts.</>} confirmLabel="Delete lead" onCancel={() => setConfirmDelete(null)} onConfirm={() => deleteOne(confirmDelete)} />}
      {selected.size > 0 && <BulkBar count={selected.size} noun="lead" onClear={() => { setSelected(new Set()); setBulkMenu(null); }}>
        <button onClick={() => setBulkMenu((menu) => menu === "status" ? null : "status")} className="flex h-10 items-center gap-2 rounded-full px-4 text-small font-semibold text-paper hover:bg-paper/10">Change status<ChevronDown size={15} strokeWidth={1.75} /></button>
        <button onClick={() => setBulkMenu((menu) => menu === "assignee" ? null : "assignee")} className="flex h-10 items-center gap-2 rounded-full px-4 text-small font-semibold text-paper hover:bg-paper/10"><UserRound size={16} strokeWidth={1.75} />Assign to<ChevronDown size={15} strokeWidth={1.75} /></button>
        <button onClick={() => { setBulkMenu(null); setBulkDeleteOpen(true); }} className="flex h-10 items-center gap-2 rounded-full px-4 text-small font-semibold text-paper hover:bg-paper/10"><Trash2 size={16} strokeWidth={1.75} />Delete</button>
      </BulkBar>}
      {bulkMenu && selected.size > 0 && <div className="fixed bottom-24 left-1/2 z-50 w-60 -translate-x-1/2 animate-nav-in rounded-md bg-paper p-1.5 shadow-md ring-1 ring-line">
          {bulkMenu === "status" ? <SearchSelect className="w-full" size="sm" value="" onChange={(value) => changeSelectedStatus(value)} options={statuses.map((item) => ({ value: item.name, label: item.name, leading: <span className={`h-2 w-2 shrink-0 rounded-full ${statusDot(item.name)}`} /> }))} placeholder="Choose status" ariaLabel="Change lead status" /> : <SearchSelect className="w-full" size="sm" value="" onChange={(value) => assignSelected(value === "Unassigned" ? "" : value)} options={[...staff.map((s) => s.name), "Unassigned"]} placeholder="Choose assignee" searchable ariaLabel="Assign leads" />}
        </div>}
      {bulkDeleteOpen && <ConfirmDialog title={`Delete ${selected.size} leads?`} body="These leads will be removed. Contacts stay in Contacts." confirmLabel={`Delete ${selected.size} leads`} onCancel={() => setBulkDeleteOpen(false)} onConfirm={deleteSelected} />}
      {importOpen && <ImportDialog kind="leads" onClose={() => setImportOpen(false)} onDone={({ added, ids }) => { setImportOpen(false); setSelected(new Set(ids)); setUndoToast({ message: `${added} leads imported` }); refresh(); }} />}
      {undoToast && selected.size === 0 && <UndoToast message={undoToast.message} onUndo={undoToast.onUndo} onDone={clearToast} />}
  </main>;
}
